import React, { useEffect, useMemo, useState } from 'react';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ExternalLink, Eye, RefreshCw, Search } from 'lucide-react';

const GET_SUPPORT_INBOUND_QUEUE = gql`
  query GetSupportInboundQueue(
    $limit: Int
    $offset: Int
    $queueStatus: String
    $processed: String
    $search: String
  ) {
    getSupportInboundQueue(
      limit: $limit
      offset: $offset
      queueStatus: $queueStatus
      processed: $processed
      search: $search
    ) {
      messages {
        id
        receivedAt
        sender
        recipient
        subject
        path
        processed
        queueStatus
        queueAssignedTo
        queueCreatedAt
        queueUpdatedAt
        ticketId
      }
      totalCount
      stats {
        total
        pendingInbound
        processedInbound
        queueNew
        queueRouted
        queueErrors
        queueIgnored
      }
    }
  }
`;

const GET_SUPPORT_INBOUND_MESSAGE = gql`
  query GetSupportInboundMessage($inboundId: String!, $includeRaw: Boolean) {
    getSupportInboundMessage(inboundId: $inboundId, includeRaw: $includeRaw) {
      id
      receivedAt
      sender
      recipient
      subject
      path
      processed
      queueStatus
      queueAssignedTo
      queueCreatedAt
      queueUpdatedAt
      ticketId
      s3Bucket
      s3Key
      s3Fetched
      s3Error
      bodyPreview
      rawMessage
    }
  }
`;

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const truncate = (value: string | null | undefined, max = 100) => {
  const text = `${value || ''}`.trim();
  if (!text) return '—';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
};

const statusBadgeClass = (status: string | null | undefined) => {
  const normalized = `${status || ''}`.toLowerCase();
  if (normalized === 'new') return 'bg-blue-100 text-blue-800 border-blue-200';
  if (normalized === 'routed') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (normalized === 'error') return 'bg-red-100 text-red-800 border-red-200';
  if (normalized.startsWith('ignored')) return 'bg-slate-100 text-slate-700 border-slate-300';
  return 'bg-amber-100 text-amber-800 border-amber-200';
};

export const SupportInboundQueueView: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [queueStatusFilter, setQueueStatusFilter] = useState('all');
  const [processedFilter, setProcessedFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInboundId, setSelectedInboundId] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery(GET_SUPPORT_INBOUND_QUEUE, {
    variables: {
      limit: pageSize,
      offset: page * pageSize,
      queueStatus: queueStatusFilter === 'all' ? undefined : queueStatusFilter,
      processed: processedFilter === 'all' ? undefined : processedFilter,
      search: searchTerm || undefined,
    },
    fetchPolicy: 'network-only',
    pollInterval: 45000,
  });

  const [
    fetchDetail,
    { data: detailData, loading: detailLoading, error: detailError },
  ] = useLazyQuery(GET_SUPPORT_INBOUND_MESSAGE, { fetchPolicy: 'network-only' });

  useEffect(() => {
    if (!selectedInboundId) return;
    fetchDetail({
      variables: {
        inboundId: selectedInboundId,
        includeRaw: false,
      },
    });
  }, [fetchDetail, selectedInboundId]);

  const result = data?.getSupportInboundQueue || {};
  const messages = Array.isArray(result?.messages) ? result.messages : [];
  const totalCount = Number(result?.totalCount || 0);
  const stats = result?.stats || {};
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const detail = detailData?.getSupportInboundMessage;

  const showingRange = useMemo(() => {
    if (totalCount === 0) return { start: 0, end: 0 };
    return {
      start: page * pageSize + 1,
      end: Math.min((page + 1) * pageSize, totalCount),
    };
  }, [page, pageSize, totalCount]);

  const runSearch = () => {
    setPage(0);
    setSearchTerm(searchInput.trim());
  };

  const resetSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(0);
  };

  const openMessage = (inboundId: string) => {
    setSelectedInboundId(inboundId);
  };

  const closeMessage = () => {
    setSelectedInboundId(null);
  };

  const refreshAll = () => {
    refetch();
    if (selectedInboundId) {
      fetchDetail({
        variables: {
          inboundId: selectedInboundId,
          includeRaw: false,
        },
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-4 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load support inbound queue: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-900">{stats.total || 0}</div>
            <div className="text-sm text-gray-600">Inbound Total</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-amber-700">{stats.pendingInbound || 0}</div>
            <div className="text-sm text-gray-600">Unprocessed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-emerald-700">{stats.processedInbound || 0}</div>
            <div className="text-sm text-gray-600">Processed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-700">{stats.queueNew || 0}</div>
            <div className="text-sm text-gray-600">Queue New</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-700">{stats.queueRouted || 0}</div>
            <div className="text-sm text-gray-600">Queue Routed</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-700">{stats.queueErrors || 0}</div>
            <div className="text-sm text-gray-600">Queue Errors</div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-[560px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            className="pl-10"
            placeholder="Search id, sender, recipient, subject, queue status"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              runSearch();
            }}
          />
        </div>
        <Button onClick={runSearch} variant="outline" size="sm">Search</Button>
        <Button onClick={resetSearch} variant="outline" size="sm">Clear</Button>

        <Select
          value={queueStatusFilter}
          onValueChange={(value) => {
            setQueueStatusFilter(value);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Queue status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All queue statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="routed">Routed</SelectItem>
            <SelectItem value="error">Error</SelectItem>
            <SelectItem value="ignored">Ignored</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={processedFilter}
          onValueChange={(value) => {
            setProcessedFilter(value);
            setPage(0);
          }}
        >
          <SelectTrigger className="w-[170px]">
            <SelectValue placeholder="Processing state" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All inbound rows</SelectItem>
            <SelectItem value="pending">Unprocessed only</SelectItem>
            <SelectItem value="processed">Processed only</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={pageSize.toString()}
          onValueChange={(value) => {
            setPage(0);
            setPageSize(Number.parseInt(value, 10));
          }}
        >
          <SelectTrigger className="w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="25">25 / page</SelectItem>
            <SelectItem value="50">50 / page</SelectItem>
            <SelectItem value="100">100 / page</SelectItem>
          </SelectContent>
        </Select>

        <Button onClick={refreshAll} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Received</TableHead>
              <TableHead>Sender</TableHead>
              <TableHead>Recipient</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Queue</TableHead>
              <TableHead>Processed</TableHead>
              <TableHead>Ticket</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {messages.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                  No inbound messages found for this filter.
                </TableCell>
              </TableRow>
            )}

            {messages.map((message: any) => (
              <TableRow key={message.id}>
                <TableCell className="text-sm text-gray-600 whitespace-nowrap">
                  {formatDateTime(message.receivedAt)}
                </TableCell>
                <TableCell className="max-w-[240px]">
                  <div className="text-sm font-medium">{truncate(message.sender, 80)}</div>
                </TableCell>
                <TableCell className="max-w-[260px]">
                  <div className="text-sm">{truncate(message.recipient, 90)}</div>
                </TableCell>
                <TableCell className="max-w-[360px]">
                  <div className="font-medium">{truncate(message.subject, 110)}</div>
                  <div className="text-xs text-gray-500 mt-1">ID: {message.id}</div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={statusBadgeClass(message.queueStatus)}>
                    {message.queueStatus || 'untracked'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {message.processed ? (
                    <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200">Yes</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">No</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {message.ticketId ? (
                    <a
                      href={`/admin/help?ticketId=${message.ticketId}`}
                      className="text-blue-700 hover:text-blue-900 inline-flex items-center gap-1 text-sm"
                    >
                      #{message.ticketId}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openMessage(message.id)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Showing {showingRange.start} to {showingRange.end} of {totalCount} rows
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(0)} disabled={page === 0}>First</Button>
          <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>Previous</Button>
          <span className="text-sm">Page {page + 1} of {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>Next</Button>
          <Button variant="outline" size="sm" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>Last</Button>
        </div>
      </div>

      <div className="text-xs text-gray-500">
        Queue auto-refreshes every 45 seconds.
      </div>

      <Dialog open={Boolean(selectedInboundId)} onOpenChange={(open: boolean) => { if (!open) closeMessage(); }}>
        <DialogContent className="max-w-[1100px] w-[96vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Inbound Mail Detail</DialogTitle>
          </DialogHeader>

          {detailLoading && (
            <div className="space-y-2">
              <Skeleton className="h-6 w-60" />
              <Skeleton className="h-48 w-full" />
            </div>
          )}

          {detailError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Failed to load inbound message detail: {detailError.message}
              </AlertDescription>
            </Alert>
          )}

          {!detailLoading && !detailError && detail && (
            <div className="space-y-4">
              <div className="rounded-md border p-3 bg-slate-50 space-y-2 text-sm">
                <div><span className="font-semibold">Inbound ID:</span> {detail.id}</div>
                <div><span className="font-semibold">Received:</span> {formatDateTime(detail.receivedAt)}</div>
                <div><span className="font-semibold">From:</span> {detail.sender || '—'}</div>
                <div><span className="font-semibold">To:</span> {detail.recipient || '—'}</div>
                <div><span className="font-semibold">Subject:</span> {detail.subject || '—'}</div>
                <div><span className="font-semibold">Queue Status:</span> {detail.queueStatus || 'untracked'}</div>
                <div><span className="font-semibold">Queue Assigned:</span> {detail.queueAssignedTo || '—'}</div>
                <div><span className="font-semibold">Processed:</span> {detail.processed ? 'yes' : 'no'}</div>
                <div><span className="font-semibold">DB Path:</span> {detail.path || '—'}</div>
                <div><span className="font-semibold">S3 Bucket:</span> {detail.s3Bucket || '—'}</div>
                <div><span className="font-semibold">S3 Key:</span> {detail.s3Key || '—'}</div>
                <div><span className="font-semibold">S3 Fetch:</span> {detail.s3Fetched ? 'success' : 'not available'}</div>
                {detail.s3Error && (
                  <div className="text-red-700">
                    <span className="font-semibold">S3 Error:</span> {detail.s3Error}
                  </div>
                )}
                {detail.ticketId ? (
                  <div>
                    <a
                      href={`/admin/help?ticketId=${detail.ticketId}`}
                      className="text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
                    >
                      Open support ticket #{detail.ticketId}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                ) : null}
              </div>

              <div className="space-y-2">
                <div className="text-sm font-semibold">S3 Body Preview</div>
                {!detail.bodyPreview ? (
                  <div className="text-sm text-gray-500 border rounded-md p-3 bg-slate-50">
                    No body preview available.
                  </div>
                ) : (
                  <pre className="text-xs leading-5 whitespace-pre-wrap break-words border rounded-md p-3 bg-black text-slate-100 overflow-x-auto">
                    {detail.bodyPreview}
                  </pre>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
